import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import * as XLSX from 'xlsx';
import {
  applyShortlist,
  clearShortlist,
  getShortlistReview,
  importShortlist,
  setShortlistResolved,
  type ShortlistImportResult,
  type ShortlistImportSheet,
  type ShortlistReviewArchitect,
  type ShortlistReviewEntry,
} from '../lib/api';
import { WAF_YELLOW_DARK } from '../theme';

// Fixed map from judging-workbook sheet name → template key. Keys here are
// pre-normalised (trimmed + lower-cased) so lookup is case-insensitive.
const SHEET_TEMPLATE_MAP: Record<string, string> = {
  'waf shortlisted': 'waf-shortlisted',
  'interiors shortlisted': 'interiors-shortlisted',
  'waf category change': 'waf-shortlisted-category-change',
  'interiors category change': 'interiors-shortlisted-category-change',
  'waf entered in both': 'waf-shortlisted-added-category',
  'interiors entered in both': 'interiors-shortlisted-added-category',
};

// Primary (WAF-yellow) button style used across the app for headline actions.
const primaryBtnSx = {
  bgcolor: WAF_YELLOW_DARK,
  color: '#111827',
  '&:hover': { bgcolor: '#E6B800' },
} as const;

/** Convert an arbitrary column header into a camelCase variable key.
 *  e.g. "Original Category" → "originalCategory", "New Category" → "newCategory". */
function toCamelCase(header: string): string {
  const words = header
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  return words
    .map((w, i) =>
      i === 0
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join('');
}

interface ParsedSheet {
  /** Original sheet name as it appeared in the workbook. */
  sheetName: string;
  payload: ShortlistImportSheet;
}

interface ParseOutcome {
  sheets: ShortlistImportSheet[];
  unknownSheets: string[];
  totalRows: number;
}

/** Parse a judging workbook in the browser into the import payload. */
function parseWorkbook(wb: XLSX.WorkBook): ParseOutcome {
  const parsedSheets: ParsedSheet[] = [];
  const unknownSheets: string[] = [];
  let totalRows = 0;

  for (const sheetName of wb.SheetNames) {
    const key = SHEET_TEMPLATE_MAP[sheetName.trim().toLowerCase()];
    if (!key) {
      unknownSheets.push(sheetName);
      continue;
    }

    const ws = wb.Sheets[sheetName];
    // Array-of-arrays so we keep header text exactly and can find the code col.
    const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, {
      header: 1,
      blankrows: false,
      defval: '',
    });
    if (matrix.length < 2) continue; // need a header row + at least one data row

    const headers = (matrix[0] as unknown[]).map((h) => String(h ?? '').trim());

    // Prefer the column whose header is "code" (case-insensitive); fall back to
    // column B (index 1), which is where it lives in their sheets.
    let codeIdx = headers.findIndex((h) => h.toLowerCase() === 'code');
    if (codeIdx === -1) codeIdx = headers.length > 1 ? 1 : 0;

    const rows: ShortlistImportSheet['rows'] = [];
    for (let r = 1; r < matrix.length; r++) {
      const row = matrix[r] as unknown[];
      const code = String(row[codeIdx] ?? '').trim();
      if (!code) continue; // skip blank-code rows

      const vars: Record<string, string> = {};
      for (let c = 0; c < headers.length; c++) {
        if (c === codeIdx) continue;
        const header = headers[c];
        if (!header) continue;
        const camel = toCamelCase(header);
        if (!camel) continue;
        const value = String(row[c] ?? '').trim();
        if (value) vars[camel] = value;
      }

      rows.push(Object.keys(vars).length ? { code, vars } : { code });
    }

    if (rows.length === 0) continue;
    totalRows += rows.length;
    parsedSheets.push({ sheetName, payload: { template_key: key, rows } });
  }

  return {
    sheets: parsedSheets.map((s) => s.payload),
    unknownSheets,
    totalRows,
  };
}

export function Onboarding() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ShortlistImportResult | null>(null);
  const [unknownSheets, setUnknownSheets] = useState<string[]>([]);
  const [showUnmatched, setShowUnmatched] = useState(false);

  const [architects, setArchitects] = useState<ShortlistReviewArchitect[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);

  // Confirm dialog: 'clear' | null
  const [confirm, setConfirm] = useState<'clear' | null>(null);

  const loadReview = useCallback(async () => {
    setError(null);
    try {
      const data = await getShortlistReview();
      setArchitects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load review');
    }
  }, []);

  useEffect(() => {
    void loadReview();
  }, [loadReview]);

  // Flatten entries across architects and bucket them by action for the
  // sectioned "Data changes" review.
  const entries = useMemo(() => architects.flatMap((a) => a.entries), [architects]);
  const sections = useMemo(
    () => ({
      shortlist: entries.filter((e) => e.action === 'shortlist'),
      category_change: entries.filter((e) => e.action === 'category_change'),
      entered_both: entries.filter((e) => e.action === 'entered_both'),
    }),
    [entries]
  );
  const appliedCount = useMemo(() => entries.filter((e) => e.applied).length, [entries]);
  const unresolvedBoth = useMemo(
    () => sections.entered_both.filter((e) => !e.resolved).length,
    [sections]
  );

  const onFilePicked = async (file: File | undefined) => {
    if (!file) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    setUnknownSheets([]);
    setShowUnmatched(false);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const { sheets, unknownSheets: unknown, totalRows } = parseWorkbook(wb);
      setUnknownSheets(unknown);

      if (sheets.length === 0) {
        setError(
          unknown.length
            ? `No recognised sheets found. Skipped: ${unknown.join(', ')}.`
            : 'No rows with a code were found in this workbook.'
        );
        return;
      }

      const result = await importShortlist({ sheets });
      setImportResult(result);
      setSnack(
        `Imported ${result.matched} of ${totalRows} row(s) across ${result.templates_seen} template(s).`
      );
      await loadReview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
      // Reset the input so re-selecting the same file fires onChange again.
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    setError(null);
    try {
      if (confirm === 'clear') {
        const res = await clearShortlist();
        setImportResult(null);
        setUnknownSheets([]);
        setSnack(`Cleared ${res.cleared} staged record(s).`);
        await loadReview();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const onApply = async () => {
    setApplying(true);
    setError(null);
    try {
      const res = await applyShortlist();
      setSnack(
        `Applied — ${res.shortlisted} shortlisted, ${res.category_changed} categor${res.category_changed === 1 ? 'y' : 'ies'} changed.`
      );
      await loadReview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const onResolve = async (assignmentId: number, resolved: boolean) => {
    try {
      await setShortlistResolved(assignmentId, resolved);
      await loadReview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update resolved state');
    }
  };

  const renderSection = (
    title: string,
    rows: ShortlistReviewEntry[],
    opts: { showCategories?: boolean; flagged?: boolean } = {}
  ) => {
    if (rows.length === 0) return null;
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {title} ({rows.length})
        </Typography>
        <Paper variant="outlined">
          {rows.map((e) => (
            <Stack
              key={e.assignment_id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              sx={{ px: 1.5, py: 0.75, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}
            >
              <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 130 }}>
                {e.nomination_code || '—'}
              </Typography>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={e.project_title ?? ''}>
                {e.project_title || '(no title)'}
              </Typography>
              {opts.showCategories && (
                <Typography variant="caption" color="text.secondary">
                  {e.current_category || '—'}
                  <Box component="span" sx={{ mx: 0.5 }}>→</Box>
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{e.new_category || '—'}</Box>
                </Typography>
              )}
              {e.applied && <Chip size="small" color="success" label="applied" />}
              {opts.flagged && (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={e.resolved}
                      onChange={(_, v) => void onResolve(e.assignment_id, v)}
                    />
                  }
                  label="Resolved"
                />
              )}
            </Stack>
          ))}
        </Paper>
      </Box>
    );
  };

  const confirmCopy: Record<'clear', { title: string; body: string; cta: string }> = {
    clear: {
      title: 'Clear all staged data?',
      body: 'This discards every staged shortlist record so you can re-import from scratch. This cannot be undone.',
      cta: 'Clear all',
    },
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h3" component="h1" align="center">
        Shortlist onboarding
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ---- Import card ---- */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">Import judging workbook</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload the judging <code>.xlsx</code>. Each sheet maps to an email
              template; each row is an entry matched by code.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={importing || busy}
              onClick={() => fileInputRef.current?.click()}
              sx={primaryBtnSx}
            >
              {importing ? 'Importing…' : 'Upload .xlsx'}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteSweepIcon />}
              disabled={importing || busy}
              onClick={() => setConfirm('clear')}
            >
              Clear all
            </Button>
          </Stack>
        </Stack>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          hidden
          onChange={(e) => void onFilePicked(e.target.files?.[0])}
        />

        {unknownSheets.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Skipped {unknownSheets.length} unrecognised sheet(s):{' '}
            {unknownSheets.join(', ')}. These were not imported.
          </Alert>
        )}

        {importResult && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Chip color="success" label={`${importResult.matched} matched`} />
              <Chip
                color={importResult.unmatched ? 'warning' : 'default'}
                label={`${importResult.unmatched} unmatched`}
              />
              <Chip label={`${importResult.templates_seen} template(s)`} />
              <Chip label={`${importResult.architects_affected} architect(s) affected`} />
            </Stack>

            {importResult.per_template.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Per-template breakdown
                </Typography>
                <List dense disablePadding>
                  {importResult.per_template.map((t) => (
                    <ListItem key={t.template_key} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={t.template_key}
                        secondary={`${t.matched} matched`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {importResult.unknown_template_keys.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Backend did not recognise these template keys:{' '}
                {importResult.unknown_template_keys.join(', ')}.
              </Alert>
            )}

            {importResult.unmatched_codes.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  onClick={() => setShowUnmatched((v) => !v)}
                  endIcon={showUnmatched ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {showUnmatched ? 'Hide' : 'Show'} {importResult.unmatched_codes.length}{' '}
                  unmatched code(s)
                </Button>
                <Collapse in={showUnmatched}>
                  <Paper
                    variant="outlined"
                    sx={{ mt: 1, p: 1.5, maxHeight: 220, overflow: 'auto' }}
                  >
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      {importResult.unmatched_codes.map((c) => (
                        <Chip key={c} size="small" variant="outlined" label={c} />
                      ))}
                    </Stack>
                  </Paper>
                </Collapse>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* ---- Data changes (the in-between apply step) ---- */}
      {architects.length > 0 && (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6">Data changes</Typography>
              <Typography variant="body2" color="text.secondary">
                Review what this import will change, then apply. Categories and the
                Shortlisted tag are written here — before any emails are sent.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PlaylistAddCheckIcon />}
              onClick={() => void onApply()}
              disabled={applying || busy}
              sx={{ ...primaryBtnSx, flexShrink: 0 }}
            >
              {applying ? 'Applying…' : 'Apply data changes'}
            </Button>
          </Stack>

          {appliedCount > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {appliedCount} of {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} already applied.
            </Alert>
          )}
          {unresolvedBoth > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {unresolvedBoth} entr{unresolvedBoth === 1 ? 'y is' : 'ies are'} entered in two
              categories — duplicate {unresolvedBoth === 1 ? 'it' : 'them'} on the Entries page,
              then tick Resolved here.
            </Alert>
          )}

          {renderSection('Will be shortlisted', sections.shortlist)}
          {renderSection('Category changes', sections.category_change, { showCategories: true })}
          {renderSection('Entered in both — duplicate manually', sections.entered_both, {
            showCategories: true,
            flagged: true,
          })}
        </Paper>
      )}

      {/* ---- Confirm dialog ---- */}
      <Dialog open={confirm !== null} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        {confirm && (
          <>
            <DialogTitle>{confirmCopy[confirm].title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{confirmCopy[confirm].body}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => void onConfirm()}
                disabled={busy}
              >
                {confirmCopy[confirm].cta}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Stack>
  );
}
